import { useServices } from '../context/WebsiteContext';
import React, { useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight } from 'lucide-react';
import { useSmartContent } from '../context/SmartSiteContext';


function useReveal(threshold = 0.12) {
  const ref = useRef(null);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const obs = new IntersectionObserver(
      (entries) => entries.forEach((e) => { if (e.isIntersecting) e.target.classList.add('visible'); }),
      { threshold, rootMargin: '0px 0px -40px 0px' }
    );
    [el, ...el.querySelectorAll('.reveal,.reveal-left,.reveal-right,.stagger-grid')].forEach((t) => obs.observe(t));
    return () => obs.disconnect();
  }, [threshold]);
  return ref;
}

const ServicesPage = () => {
  const services = useServices();
  const listRef = useReveal(0.05);
  const ctaRef = useReveal(0.15);
  const svcData = useSmartContent('services-hero', 'hero');
  const s = svcData.items?.[0]?.data || {};

  return (
    <div className="min-h-screen bg-white" data-testid="services-page">
      {/* Hero */}
      <section className="relative py-12 sm:py-20 bg-[#1B3A5C] overflow-hidden">
        <div className="absolute inset-0 opacity-5 pattern-dots" />
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <nav className="text-sm text-blue-200/50 mb-4">
            <Link to="/" className="hover:text-[#F5A623]">Home</Link>
            <span className="mx-2">/</span>
            <span className="text-white">Services</span>
          </nav>
          <h1 className="text-4xl sm:text-5xl font-bold tracking-tight mb-4 text-white">{s.headline || s.heading || 'Our Services'}</h1>
          <p className="text-blue-100/70 max-w-2xl">
            {s.body || 'We specialize in you! Bringing your ideas to life requires us to test the limits of our Kre8ivity every day.'}
          </p>
          {s.image && <img src={s.image} alt="" className="mt-6 max-h-64 max-w-full object-contain rounded-lg"/>}
          {s.cta_text && <Link to={s.cta_link || '/shop'} className="inline-block mt-5 px-5 py-3 bg-[#F5A623] text-gray-900 rounded-full">{s.cta_text}</Link>}
        </div>
      </section>

      {/* Services */}
      <section ref={listRef} className="py-12 sm:py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="space-y-16">
            {services.map((service, index) => (
              <div
                key={service.slug}
                className="reveal grid lg:grid-cols-2 gap-12 items-center"
                data-testid={`service-${index}`}
              >
                <div className={index % 2 === 1 ? 'lg:order-2' : ''}>
                  <p className="text-[#4A90A4] text-sm font-semibold tracking-[0.2em] uppercase mb-3">
                    {String(index + 1).padStart(2, '0')}
                  </p>
                  <h2 className="text-3xl font-bold mb-2 text-gray-900">{service.name}</h2>
                  <p className="text-sm text-[#F5A623] font-medium mb-4">{service.tagline}</p>
                  <p className="text-gray-600 leading-relaxed mb-6">{service.shortDescription}</p>
                  <ul className="grid grid-cols-2 gap-3 mb-8">
                    {service.features.slice(0, 4).map((feature, i) => (
                      <li key={i} className="flex items-center text-sm text-gray-600">
                        <span className="w-2 h-2 bg-[#F5A623] rounded-full mr-3 flex-shrink-0" />
                        {feature}
                      </li>
                    ))}
                  </ul>
                  <div className="flex flex-wrap gap-3">
                    <Link
                      to={`/services/${service.slug}`}
                      className="inline-flex items-center px-6 py-3 bg-[#4A90A4] text-white font-bold rounded-full hover:bg-[#3d7a8e] transition-colors text-sm"
                      data-testid={`service-learn-more-${service.slug}`}
                    >
                      Learn More <ArrowRight className="ml-2 w-4 h-4" />
                    </Link>
                    <Link
                      to={service.button_link}
                      className="inline-flex items-center px-6 py-3 bg-[#F5A623] text-gray-900 font-bold rounded-full btn-glow text-sm"
                    >
                      {service.button_text}
                    </Link>
                  </div>
                </div>
                <div className={`${index % 2 === 1 ? 'lg:order-1' : ''}`}>
                  <Link to={`/services/${service.slug}`} className="block">
                    <div className="service-card aspect-[4/3] overflow-hidden rounded-2xl shadow-lg border border-gray-100 group">
                      <img src={service.image} alt={service.name} className="card-image w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                    </div>
                  </Link>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section ref={ctaRef} className="py-12 sm:py-20 bg-[#4A90A4]">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center reveal">
          <h2 className="text-3xl sm:text-4xl font-bold tracking-tight mb-6 text-white">
            Ready to Start Your Project?
          </h2>
          <p className="text-white/75 text-lg mb-8">
            Get a personalized quote for your custom embroidery or printing project.
          </p>
          <Link
            to="/quote/builder"
            className="inline-flex items-center justify-center px-8 py-4 bg-[#F5A623] text-gray-900 font-bold rounded-full btn-glow"
            data-testid="services-cta-btn"
          >
            Request a Quote <ArrowRight className="ml-2 w-5 h-5" />
          </Link>
        </div>
      </section>
    </div>
  );
};

export default ServicesPage;
